/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { serviceService } from "@src/@api-services/service";
import { categoryService } from "@src/@api-services/category";
import { subcategoryService } from "@src/@api-services/subcategory";
import { type SortOrderValue } from "@src/shared/constants/sort-order.constants";

interface Service {
  id: string | number;
  businessId?: string | number;
  businessName: string;
  category?: string | null;
  categoryId?: number | null;
  categoryName?: string | null;
  subCategories?: Array<{ id: number; name: string }>;
  tags?: string[] | null;
  name: string;
  description: string;
  durationMinutes?: number;
  basePrice?: number;
  price?: number;
  status?: string;
  reason?: string | null;
  media?: any[];
  images?: any[];
  isActive?: boolean;
  createdAt: string;
  updatedAt: string;
  [key: string]: any;
}

interface GetServicesParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
  categoryId?: number | string;
  orderBy?: string;
  orderDirection?: SortOrderValue;
  [key: string]: any;
}

interface SelectOption {
  value: number;
  label: string;
}

interface ServicesState {
  data: Service[];
  total: number;
  params: GetServicesParams;
  selectedService: Service | null;
  categoryOptions: SelectOption[];
  subcategoryOptions: SelectOption[];
  loading: boolean;
  catalogLoading: boolean;
  error: string | null;
}

export const getServices = createAsyncThunk(
  "services/getServices",
  async (params: GetServicesParams, { rejectWithValue }) => {
    try {
      const response = await serviceService.getServices(params);
      return {
        params,
        data: response.data.data.data,
        total: response.data.data.totalRecord,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch services",
      );
    }
  },
);

export const getService = createAsyncThunk(
  "services/getService",
  async (id: string | number, { rejectWithValue }) => {
    try {
      const response = await serviceService.getService(id);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch service",
      );
    }
  },
);

export const getServiceCategories = createAsyncThunk(
  "services/getServiceCategories",
  async (_, { rejectWithValue }) => {
    try {
      const response = await categoryService.getAllCategories({
        orderBy: "name",
        orderDirection: "ASC",
      });
      const rows = response.data?.data ?? [];
      return (Array.isArray(rows) ? rows : []).map((item: any) => ({
        value: Number(item.id),
        label: item.name,
      })) as SelectOption[];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch categories",
      );
    }
  },
);

export const getServiceSubcategories = createAsyncThunk(
  "services/getServiceSubcategories",
  async (categoryId: number | null, { rejectWithValue }) => {
    try {
      if (!categoryId) return [] as SelectOption[];

      const response = await subcategoryService.getAllSubcategories({
        categoryId,
        orderBy: "name",
        orderDirection: "ASC",
      });
      const rows = response.data?.data ?? [];
      return (Array.isArray(rows) ? rows : []).map((item: any) => ({
        value: Number(item.id),
        label: item.name,
      })) as SelectOption[];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch subcategories",
      );
    }
  },
);

export const updateServiceStatus = createAsyncThunk(
  "services/updateStatus",
  async (
    {
      id,
      status,
      rejectionReason,
    }: { id: string | number; status: string; rejectionReason?: string },
    { dispatch, getState, rejectWithValue }: any,
  ) => {
    try {
      const response = await serviceService.updateServiceStatus(id, {
        status,
        rejectionReason,
      });
      const state = getState();
      await dispatch(getServices(state.services.params));
      await dispatch(getService(id));
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update service status",
      );
    }
  },
);

export const updateServiceCatalogAction = createAsyncThunk(
  "services/updateCatalog",
  async (
    {
      id,
      categoryId,
      subCategoryIds,
      tags,
    }: {
      id: string | number;
      categoryId: number | null;
      subCategoryIds: number[];
      tags: string[];
    },
    { dispatch, rejectWithValue }: any,
  ) => {
    try {
      await Promise.all([
        serviceService.updateServiceCategory(id, { categoryId }),
        serviceService.updateServiceSubCategories(id, { subCategoryIds }),
        serviceService.updateServiceTags(id, { tags }),
      ]);

      await dispatch(getService(id));
      return true;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update service catalog",
      );
    }
  },
);

const initialState: ServicesState = {
  data: [],
  total: 0,
  params: {
    page: 1,
    pageSize: 10,
    orderBy: "id",
    orderDirection: "ASC",
  },
  selectedService: null,
  categoryOptions: [],
  subcategoryOptions: [],
  loading: false,
  catalogLoading: false,
  error: null,
};

export const servicesSlice = createSlice({
  name: "services",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedService: (state) => {
      state.selectedService = null;
      state.subcategoryOptions = [];
    },
    setParams: (state, action) => {
      state.params = { ...state.params, ...action.payload };
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getServices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getServices.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.params = action.payload.params;
        state.total = action.payload.total;
      })
      .addCase(getServices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.data = [];
        state.total = 0;
      })
      .addCase(getService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getService.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedService = action.payload;
      })
      .addCase(getService.rejected, (state) => {
        state.loading = false;
        state.selectedService = null;
      })
      .addCase(getServiceCategories.fulfilled, (state, action) => {
        state.categoryOptions = action.payload;
      })
      .addCase(getServiceSubcategories.fulfilled, (state, action) => {
        state.subcategoryOptions = action.payload;
      })
      .addCase(getServiceSubcategories.rejected, (state) => {
        state.subcategoryOptions = [];
      })
      .addCase(updateServiceCatalogAction.pending, (state) => {
        state.catalogLoading = true;
        state.error = null;
      })
      .addCase(updateServiceCatalogAction.fulfilled, (state) => {
        state.catalogLoading = false;
      })
      .addCase(updateServiceCatalogAction.rejected, (state, action) => {
        state.catalogLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearSelectedService, setParams } =
  servicesSlice.actions;

export default servicesSlice.reducer;
